import {
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  tableCellClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { PAGE } from "components/common/CONSTANTS";
import { LabelWithControl } from "components/common/LabelControl";
import { getFormattedDateWithTime } from "components/common/Utility";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "@mui/material/Pagination";
import { bulkDeleteMaxCapAction, bulkDeleteMaxCapReset } from "redux/actions/bulkDeleteMaxCapAction";
import { searchMaxCapAction, searchMaxCapReset } from "redux/actions/searchMaxCapAction";
import { addMaxCapAction, addMaxCapReset } from "redux/actions/addMaxCapAction";
import { bulkUpdateMaxCapAction, bulkUpdateMaxCapReset } from "redux/actions/bulkUpdateMaxCapAction";
import { updateSelectedMaxCapAction, updateSelectedMaxCapReset } from "redux/actions/updateSelectedMaxCapAction";
import { showNotificationDialogEmergencyOrder } from "redux/actions/notificationEmergencyOrderActions";
import { showErrorMessageByCode } from "services/commonServices";
import { MAX_CAP_LIMIT, MAX_CAP_QTY_TYPE, MAX_CAP_STATUS, MAX_CAP_FEATURE, MAX_CAP_ADD_SUCCESS, MAX_CAP_UPDATE_SUCCESS, MAX_CAP_DELETE_SUCCESS, MAX_CAP_BULK_UPDATE_TITLE, MAX_CAP_ADD_NEW_PIN_TITLE, MAX_CAP_DELETE_PIN_TITLE, MAX_CAP_SELECTION_LIMIT_MSG, MAX_CAP_UPDATE_FAILED_MSG } from "services/CONSTANTS";
import { encryptString } from "components/common/EncryptDecrypt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { StoreSelector } from "./StoreSelector";
import { MultiPinSearch } from "./MultiPinSearch";
import MaxCapMarquee from "./MaxCapMarquee";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.background.default,
    fontSize: 12,
    border: "1px solid #A9A9A9",
    fontWeight: "bold",
    whiteSpace: "nowrap",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    border: "1px solid #A9A9A9",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
  "&:last-child td, &:last-child th": { border: "1px solid #A9A9A9" },
}));

export const MaxOrderQuantityConfiguration = () => {
  const dispatch = useDispatch();
  const loggedInUser = useSelector((state) => state.user);
  const { isBulkDeleteLoading, isBulkDeleteSuccess, isBulkDeleteFail, bulkDeleteMaxCapResponse } =
    useSelector((state) => state.bulkDeleteMaxCap);
  const { isSearchLoading, isSearchSuccess, isSearchFail, searchMaxCapResponse } =
    useSelector((state) => state.searchMaxCap);
  const { isAddLoading, isAddSuccess, isAddFail, addMaxCapResponse } =
    useSelector((state) => state.addMaxCap);
  const { isBulkUpdateLoading, isBulkUpdateSuccess, isBulkUpdateFail, bulkUpdateMaxCapResponse } =
    useSelector((state) => state.bulkUpdateMaxCap);
  const { isUpdateSelectedLoading, isUpdateSelectedSuccess, isUpdateSelectedFail, updateSelectedMaxCapResponse } =
    useSelector((state) => state.updateSelectedMaxCap);

  const [packIds, setPackIds] = useState("");
  const [storeId, setStoreId] = useState("");
  const [searchResultData, setSearchResultData] = useState([]);
  const [allPagesData, setAllPagesData] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchError, setSearchError] = useState("");
  const [editQuantities, setEditQuantities] = useState({});

  const [globalPin, setGlobalPin] = useState("");
  const [globalMaxQty, setGlobalMaxQty] = useState("");
  const [globalPinError, setGlobalPinError] = useState("");
  const [globalQtyError, setGlobalQtyError] = useState("");
  const [showUpdateAllModal, setShowUpdateAllModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePin, setDeletePin] = useState("");
  const [deleteMin, setDeleteMin] = useState("");
  const [deletePinError, setDeletePinError] = useState("");
  const [deleteMinError, setDeleteMinError] = useState("");

  const [showAddModal, setShowAddModal] = useState(false);
  const [addItemId, setAddItemId] = useState("");
  const [addPackId, setAddPackId] = useState("");
  const [addMaxQty, setAddMaxQty] = useState("");
  const [addPackIdError, setAddPackIdError] = useState("");
  const [addMaxQtyError, setAddMaxQtyError] = useState("");
  const [addItemIdError, setAddItemIdError] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [searchParams, setSearchParams] = useState(null);
  const [fetchedPages, setFetchedPages] = useState({});

  const [showSpinner] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState("");
  const tableContainerRef = useRef(null);

  const hasEmptyQty = Object.values(editQuantities).some((emptyQty) => emptyQty === "");

  const handleSearch = (offset = 0) => {
    setSearchError("");
    setApiErrorMessage("");
    if (offset === 0) {
      setPage(0);
      setSelectedRows([]);
      setEditQuantities({});
      setSearchResultData([]);
      setFetchedPages({});
      setAllPagesData({});
    }

    if (!packIds.trim()) {
      setSearchError("PIN is required");
      return;
    }

    const parsed = packIds.trim()
      ? packIds
          .split(",")
          .map((x) => x.trim())
          .filter((x) => x && !isNaN(x))
      : [];

    if (packIds.trim() && parsed.length === 0) {
      setSearchError("Please enter valid numeric PINs");
      return;
    }
    if (parsed.length > 10) {
      setSearchError("Maximum 10 PINs allowed");
      return;
    }
    if (parsed.some((p) => p.length < 9)) {
      setSearchError("Each PIN must be 9 digits");
      return;
    }

    let params = {
      feature: MAX_CAP_FEATURE,
      limit: MAX_CAP_LIMIT,
      status: MAX_CAP_STATUS,
      "itemNumber[in]": parsed.join(","),
    };

    if (storeId) {
      params.storeid = storeId;
    }

    setSearchParams(params);
    dispatch(searchMaxCapAction({ ...params, offset: offset }));
  };

  useEffect(() => {
    if (isSearchSuccess) {
      const data = searchMaxCapResponse?.data?.itemConfigurations || [];
      const metadata = searchMaxCapResponse?.data?.metadata;
      if (data.length === 0 && Object.keys(allPagesData).length === 0) {
        setApiErrorMessage("No records found");
        setSearchResultData([]);
        setTotalCount(0);
      } else {
        const currentPage = Math.floor((metadata?.offset || 0) / rowsPerPage);
        const mappedData = data.map((item, index) => ({
          id: `${item.packId}_${item.storeId}_${currentPage}_${index}`,
          packId: item.packId,
          itemId: item.itemId || "",
          storeId: item.storeId,
          maxQuantity: item.maxQuantity,
          updatedBy: item.updatedBy,
          updatedAt: item.updatedAt,
          qtyType: item.qtyType,
        }));
        const updatedPages = { ...allPagesData, [currentPage]: mappedData };
        setAllPagesData(updatedPages);
        setSearchResultData(updatedPages[page] || []);
        setFetchedPages((prev) => ({ ...prev, [currentPage]: true }));
        setTotalCount(metadata?.totalCount || mappedData.length);
      }
      dispatch(searchMaxCapReset());
    }
  }, [isSearchSuccess, searchMaxCapResponse, dispatch]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSearchFail) {
      if (searchMaxCapResponse?.response?.status === 404) {
        setApiErrorMessage("No records found");
      } else if (searchMaxCapResponse?.response?.status === 400) {
        setApiErrorMessage("");
        dispatch(showNotificationDialogEmergencyOrder("Search Failed", searchMaxCapResponse?.response?.data?.errorMessage || "Invalid search criteria.", "Ok"));
      } else {
        setApiErrorMessage("");
        const { errTitle, errContent } = showErrorMessageByCode(searchMaxCapResponse);
        dispatch(showNotificationDialogEmergencyOrder(errTitle, errContent, "Ok"));
      }
      setSearchResultData([]);
      dispatch(searchMaxCapReset());
    }
  }, [isSearchFail, searchMaxCapResponse, dispatch]);

  const handleKeyDown = (e, rowId, field) => {
    if (e.key !== "Tab") return;
    e.preventDefault();
    const currentPageIndex = searchResultData.findIndex((r) => r.id === rowId);
    const nextRow = searchResultData[currentPageIndex + 1];
    if (nextRow) {
      document.getElementById(`qty-${nextRow.id}`)?.focus();
    } else {
      const btn = document.querySelector(".parentPoButtons button:not(:disabled)");
      if (btn) btn.focus();
    }
  };

  const handleQuantityChange = (id, value) => {
    const quantityValue = value.replace(/[^0-9]/g, "");
    if (quantityValue === "" || (parseInt(quantityValue) >= 1 && parseInt(quantityValue) <= 99)) {
      const originalQty = searchResultData.find((r) => r.id === id)?.maxQuantity;
      if (quantityValue !== "" && parseInt(quantityValue) === originalQty) {
        setEditQuantities((prev) => { const next = { ...prev }; delete next[id]; return next; });
        setSelectedRows((prev) => prev.filter((r) => r !== id));
      } else {
        setEditQuantities((prev) => ({ ...prev, [id]: quantityValue }));
        if (quantityValue !== "") {
          if (!selectedRows.includes(id) && selectedRows.length >= 200) {
            dispatch(showNotificationDialogEmergencyOrder("Selection Limit", MAX_CAP_SELECTION_LIMIT_MSG, "Ok"));
            return;
          }
          setSelectedRows((prev) => prev.includes(id) ? prev : [...prev, id]);
        } else {
          setSelectedRows((prev) => prev.filter((r) => r !== id));
        }
      }
    }
  };

  const handleUpdate = async () => {
    if (selectedRows.length === 0) return;

    // Get all rows from all fetched pages
    const allRows = Object.values(allPagesData).flat();

    const items = selectedRows.map((id) => {
      const row = allRows.find((r) => r.id === id);
      return {
        packId: row.packId,
        storeId: String(row.storeId),
        quantity: editQuantities[id] !== undefined ? editQuantities[id] : String(row.maxQuantity),
      };
    });

    // Group by packId + quantity
    const grouped = {};
    items.forEach(({ packId, storeId, quantity }) => {
      const key = `${packId}_${quantity}`;
      if (!grouped[key]) {
        grouped[key] = { packId, quantity, stores: [] };
      }
      grouped[key].stores.push({ storeId });
    });

    const itemConfigurations = Object.values(grouped).map((item) => ({
      packId: item.packId,
      quantity: item.quantity,
      qtyType: MAX_CAP_QTY_TYPE,
      status: MAX_CAP_STATUS,
      stores: item.stores,
    }));

    const encryptedEmail = await encryptString(loggedInUser?.loginResponse?.email);
    const payload = {
      feature: MAX_CAP_FEATURE,
      updatedBy: encryptedEmail,
      itemConfigurations,
    };
    dispatch(updateSelectedMaxCapAction(payload));
  };

  useEffect(() => {
    if (isUpdateSelectedSuccess) {
      setSelectedRows([]);
      setEditQuantities({});
      dispatch(
        showNotificationDialogEmergencyOrder(
          "Update Successful",
          MAX_CAP_UPDATE_SUCCESS,
          "Done"
        )
      );
      dispatch(updateSelectedMaxCapReset());
      handleSearch();
    }
  }, [isUpdateSelectedSuccess, dispatch]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isUpdateSelectedFail) {
      setSearchError("");
      setApiErrorMessage("");
      if (updateSelectedMaxCapResponse?.response?.status === 404) {
        const errorMsg = updateSelectedMaxCapResponse?.response?.data?.errorMessage || "";
        if (errorMsg.includes("packId(s) not found in maxOrderQuantity configuration")) {
          dispatch(showNotificationDialogEmergencyOrder("Update Failed", "PIN and Store ID combination not configured.", "Ok"));
        } else {
          dispatch(showNotificationDialogEmergencyOrder("Update Failed", MAX_CAP_UPDATE_FAILED_MSG, "Ok"));
        }
      } else if (updateSelectedMaxCapResponse?.response?.status === 400) {
        dispatch(showNotificationDialogEmergencyOrder("Update Failed", updateSelectedMaxCapResponse?.response?.data?.errorMessage || MAX_CAP_UPDATE_FAILED_MSG, "Ok"));
      } else {
        const { errTitle, errContent } = showErrorMessageByCode(updateSelectedMaxCapResponse);
        dispatch(showNotificationDialogEmergencyOrder(errTitle, errContent, "Ok"));
      }
      dispatch(updateSelectedMaxCapReset());
    }
  }, [isUpdateSelectedFail, updateSelectedMaxCapResponse, dispatch]);

  const handleGlobalUpdate = async () => {
    setGlobalPinError("");
    setGlobalQtyError("");
    if (!globalPin || isNaN(globalPin)) { setGlobalPinError("PIN is required"); return; }
    if (globalPin.length < 9) { setGlobalPinError("PIN must be 9 digits"); return; }
    const qty = parseInt(globalMaxQty);
    if (isNaN(qty) || qty < 0 || qty > 99) { setGlobalQtyError("Quantity is required"); return; }

    const encryptedEmail = await encryptString(loggedInUser?.loginResponse?.email);
    const payload = {
      feature: MAX_CAP_FEATURE,
      updatedBy: encryptedEmail,
      itemConfigurations: [
        {
          packId: parseInt(globalPin),
          quantity: globalMaxQty,
          qtyType: MAX_CAP_QTY_TYPE,
          status: MAX_CAP_STATUS,
          stores: [{ storeId: "@all" }],
        },
      ],
    };
    dispatch(bulkUpdateMaxCapAction(payload));
  };

  useEffect(() => {
    if (isBulkUpdateSuccess) {
      setShowUpdateAllModal(false);
      setGlobalPin("");
      setGlobalMaxQty("");
      setGlobalPinError("");
      setGlobalQtyError("");
      setSearchResultData([]);
      setPackIds("");
      setStoreId("");
      setSelectedRows([]);
      setEditQuantities({});
      setPage(0);
      setSearchError("");
      setApiErrorMessage("");
      dispatch(
        showNotificationDialogEmergencyOrder(
          "Update Successful",
          MAX_CAP_UPDATE_SUCCESS,
          "Done"
        )
      );
      dispatch(bulkUpdateMaxCapReset());
    }
  }, [isBulkUpdateSuccess, dispatch]);

  useEffect(() => {
    if (isBulkUpdateFail) {
      setShowUpdateAllModal(false);
      setGlobalPin("");
      setGlobalMaxQty("");
      setGlobalPinError("");
      setGlobalQtyError("");
      setSearchError("");
      setApiErrorMessage("");
      if (bulkUpdateMaxCapResponse?.response?.status === 404) {
        const errorMsg = bulkUpdateMaxCapResponse?.response?.data?.errorMessage || "";
        if (errorMsg.includes("packId not found in maxOrderQuantity configuration") || errorMsg.includes("No records found to update for packId")) {
          dispatch(showNotificationDialogEmergencyOrder("Update Failed", "PIN and Store ID combination not configured.", "Ok"));
        } else {
          dispatch(showNotificationDialogEmergencyOrder("Update Failed", MAX_CAP_UPDATE_FAILED_MSG, "Ok"));
        }
      } else if (bulkUpdateMaxCapResponse?.response?.status === 400) {
        dispatch(showNotificationDialogEmergencyOrder("Update Failed", bulkUpdateMaxCapResponse?.response?.data?.errorMessage || MAX_CAP_UPDATE_FAILED_MSG, "Ok"));
      } else {
        const { errTitle, errContent } = showErrorMessageByCode(bulkUpdateMaxCapResponse);
        dispatch(showNotificationDialogEmergencyOrder(errTitle, errContent, "Ok"));
      }
      dispatch(bulkUpdateMaxCapReset());
    }
  }, [isBulkUpdateFail, bulkUpdateMaxCapResponse, dispatch]);

  const handleDeleteSubmit = async () => {
    setDeletePinError("");
    setDeleteMinError("");
    if (!deletePin || isNaN(deletePin)) { setDeletePinError("PIN is required"); return; }
    if (deletePin.length < 9) { setDeletePinError("PIN must be 9 digits"); return; }
    if (deleteMin && deleteMin.length < 9) { setDeleteMinError("MIN must be 9 digits"); return; }

    const encryptedEmail = await encryptString(loggedInUser?.loginResponse?.email);
    const itemConfig = {
      packId: parseInt(deletePin),
      stores: [{ storeId: "@all" }],
    };
    if (deleteMin) {
      itemConfig.itemId = parseInt(deleteMin);
    }
    const payload = {
      feature: MAX_CAP_FEATURE,
      updatedBy: encryptedEmail,
      itemConfigurations: [itemConfig],
    };
    dispatch(bulkDeleteMaxCapAction(payload));
  };

  useEffect(() => {
    if (isBulkDeleteSuccess) {
      setShowDeleteModal(false);
      setDeletePin("");
      setDeleteMin("");
      setDeletePinError("");
      setSearchResultData([]);
      setPackIds("");
      setStoreId("");
      setSelectedRows([]);
      setEditQuantities({});
      setPage(0);
      setSearchError("");
      setApiErrorMessage("");
      dispatch(
        showNotificationDialogEmergencyOrder(
          "Delete Successful",
          MAX_CAP_DELETE_SUCCESS,
          "Done"
        )
      );
      dispatch(bulkDeleteMaxCapReset());
    }
  }, [isBulkDeleteSuccess, dispatch]);

  useEffect(() => {
    if (isBulkDeleteFail) {
      setShowDeleteModal(false);
      setDeletePin("");
      setDeleteMin("");
      setDeletePinError("");
      setSearchError("");
      setApiErrorMessage("");
      if (bulkDeleteMaxCapResponse?.response?.status === 404) {
        dispatch(showNotificationDialogEmergencyOrder("Delete Failed", "Failed to delete.", "Ok"));
      } else if (bulkDeleteMaxCapResponse?.response?.status === 400) {
        dispatch(showNotificationDialogEmergencyOrder("Delete Failed", bulkDeleteMaxCapResponse?.response?.data?.errorMessage || "Failed to delete.", "Ok"));
      } else {
        const { errTitle, errContent } = showErrorMessageByCode(bulkDeleteMaxCapResponse);
        dispatch(showNotificationDialogEmergencyOrder(errTitle, errContent, "Ok"));
      }
      dispatch(bulkDeleteMaxCapReset());
    }
  }, [isBulkDeleteFail, bulkDeleteMaxCapResponse, dispatch]);

  const handleAddSubmit = async () => {
    setAddPackIdError("");
    setAddMaxQtyError("");
    setAddItemIdError("");
    let hasError = false;
    if (!addPackId || isNaN(addPackId)) { setAddPackIdError("PIN is required"); hasError = true; }
    else if (addPackId.length < 9) { setAddPackIdError("PIN must be 9 digits"); hasError = true; }
    if (addItemId && addItemId.length < 9) { setAddItemIdError("MIN must be 9 digits"); hasError = true; }
    const qty = parseInt(addMaxQty);
    if (isNaN(qty) || qty < 0 || qty > 99) { setAddMaxQtyError("Quantity is required"); hasError = true; }
    if (hasError) return;

    const encryptedEmail = await encryptString(loggedInUser?.loginResponse?.email);
    const itemConfig = {
      packId: parseInt(addPackId),
      quantity: addMaxQty,
      qtyType: MAX_CAP_QTY_TYPE,
      status: MAX_CAP_STATUS,
      stores: [{ storeId: "@all" }],
    };
    if (addItemId) {
      itemConfig.itemId = parseInt(addItemId);
    }
    const payload = {
      feature: MAX_CAP_FEATURE,
      updatedBy: encryptedEmail,
      itemConfigurations: [itemConfig],
    };
    dispatch(addMaxCapAction(payload));
  };

  useEffect(() => {
    if (isAddSuccess) {
      setShowAddModal(false);
      setAddItemId("");
      setAddPackId("");
      setAddMaxQty("");
      setSearchResultData([]);
      setPackIds("");
      setStoreId("");
      setSelectedRows([]);
      setEditQuantities({});
      setPage(0);
      setSearchError("");
      setApiErrorMessage("");
      dispatch(
        showNotificationDialogEmergencyOrder(
          "Add Successful",
          MAX_CAP_ADD_SUCCESS,
          "Done"
        )
      );
      dispatch(addMaxCapReset());
    }
  }, [isAddSuccess, dispatch]);

  useEffect(() => {
    if (isAddFail) {
      setShowAddModal(false);
      setAddItemId("");
      setAddPackId("");
      setAddMaxQty("");
      setAddPackIdError("");
      setAddMaxQtyError("");
      setSearchError("");
      setApiErrorMessage("");
      if (addMaxCapResponse?.response?.status === 404) {
        const errorMsg = addMaxCapResponse?.response?.data?.errorMessage || "";
        if (errorMsg.includes("packId not found in product-detail") || errorMsg.includes("PackId not found in store_catalogue_details")) {
          dispatch(showNotificationDialogEmergencyOrder("Add Failed", "Invalid PIN, please enter correct PIN.", "Ok"));
        } else if (errorMsg.includes("No locations found to configure")) {
          dispatch(showNotificationDialogEmergencyOrder("Add Failed", "PIN and Store ID combination not configured.", "Ok"));
        } else {
          dispatch(showNotificationDialogEmergencyOrder("Add Failed", errorMsg || "Failed to add an item.", "Ok"));
        }
      } else if (addMaxCapResponse?.response?.status === 400) {
        dispatch(showNotificationDialogEmergencyOrder("Add Failed", "MIN/PIN combination does not match.", "Ok"));
      } else if (addMaxCapResponse?.response?.status === 409) {
        dispatch(showNotificationDialogEmergencyOrder("Add Failed", addMaxCapResponse?.response?.data?.errorMessage || "Configuration already exists for this PIN.", "Ok"));
      } else {
        const { errTitle, errContent } = showErrorMessageByCode(addMaxCapResponse);
        dispatch(showNotificationDialogEmergencyOrder(errTitle, errContent, "Ok"));
      }
      dispatch(addMaxCapReset());
    }
  }, [isAddFail, addMaxCapResponse, dispatch]);

  return (
    <>
      <div className="mainPageStyling">
        {(showSpinner || isBulkDeleteLoading || isSearchLoading || isAddLoading || isBulkUpdateLoading || isUpdateSelectedLoading) && (
          <Backdrop
            sx={{
              color: "#fff",
              flexDirection: "column",
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
            open={true}
          >
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ marginTop: 2 }}>
              Loading...
            </Typography>
          </Backdrop>
        )}

        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography component="h1" variant="h5" sx={{ mb: 2, mr: 2 }}>
            {PAGE.ORDER_PAD_MAX_CAP.label}
          </Typography>
           <MaxCapMarquee />
        </div>

        <div style={{ marginBottom: "10px" }} className="labelControl">
          <Grid
            sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
          >
            <Grid container spacing={2} alignItems="flex-end">
              <MultiPinSearch
                mode="pin"
                packIds={packIds}
                setPackIds={(val) => { setPackIds(val); setSearchError(""); }}
                searchError={searchError}
              />
              <Grid item xs={12} sm={3} md={2}>
                <StoreSelector
                  storeId={storeId}
                  onChange={(val) => {
                    setStoreId(val);
                    setSearchError("");
                    setApiErrorMessage("");
                  }}
                />
              </Grid>
              <MultiPinSearch
                packIds={packIds}
                setPackIds={(val) => { setPackIds(val); setSearchError(""); }}
                searchError={searchError}
                onSearch={() => handleSearch()}
                onAddNew={() => { setShowAddModal(true); }}
                onUpdateAll={() => { setShowUpdateAllModal(true); setGlobalPin(""); setGlobalMaxQty(""); setGlobalPinError(""); setGlobalQtyError(""); }}
                onDelete={() => { setShowDeleteModal(true); setDeletePin(""); setDeleteMin(""); setDeletePinError(""); }}
              />
            </Grid>
          </Grid>
        </div>
        {searchResultData.length > 0 ? (
          <div style={{ width: "100%", height: "calc(100vh - 275px)" }}>
            <TableContainer
              component={Paper}
              className="tableStructure"
              style={{ height: "100%", overflow: "auto" }}
              ref={tableContainerRef}
            >
              <Table sx={{ minWidth: 700 }} stickyHeader>
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell className="maxCapCellWidth">PIN</StyledTableCell>
                    <StyledTableCell className="maxCapCellWidth">MIN</StyledTableCell>
                    <StyledTableCell className="maxCapCellWidth">Store ID</StyledTableCell>
                    <StyledTableCell>Max Quantity</StyledTableCell>
                    <StyledTableCell className="maxCapUpdatedByFlexCell">Updated By</StyledTableCell>
                    <StyledTableCell>Updated At</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {searchResultData.map((row) => (
                  <StyledTableRow key={row.id} sx={{ backgroundColor: editQuantities[row.id] !== undefined && editQuantities[row.id] !== "" ? "rgba(200, 230, 201, 1)!important" : "inherit" }}>
                      <StyledTableCell className="maxCapCellWidth">{row.packId}</StyledTableCell>
                      <StyledTableCell className="maxCapCellWidth">{row.itemId}</StyledTableCell>
                      <StyledTableCell className="maxCapCellWidth">{row.storeId}</StyledTableCell>
                      <StyledTableCell className="maxCapQtyCellWidth">
                        <span style={{ display: "flex", alignItems: "center" }}>
                          <TextField
                            id={`qty-${row.id}`}
                            variant="outlined"
                            size="small"
                            value={
                              editQuantities[row.id] !== undefined
                                ? editQuantities[row.id]
                                : row.maxQuantity
                            }
                            onChange={(e) =>
                              handleQuantityChange(row.id, e.target.value)
                            }
                            onKeyDown={(e) => handleKeyDown(e, row.id)}
                            error={editQuantities[row.id] === ""}
                            inputProps={{
                              maxLength: 2,
                              style: {
                                width: 50,
                                padding: "2px 4px",
                                fontSize: 12,
                              },
                            }}
                            sx={{ "& .MuiOutlinedInput-root": { height: "20px" } }}
                          />
                          {editQuantities[row.id] === "" && (
                            <Tooltip title="Qty is required" arrow>
                              <InfoOutlinedIcon sx={{ color: "#F28C28", fontSize: "16px", ml: 0.5 }} />
                            </Tooltip>
                          )}
                        </span>
                      </StyledTableCell>
                      <StyledTableCell className="maxCapUpdatedByFlexCell">{row.updatedBy}</StyledTableCell>
                      <StyledTableCell>
                        {getFormattedDateWithTime(row.updatedAt)}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        ) : (
          <div
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              color: "red",
              marginBottom: "10px",
            }}
          >
            {apiErrorMessage}
          </div>
        )}
      </div>

      {searchResultData.length > 0 && (
        <>
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page + 1}
            onChange={(e, newPage) => {
              setPage(newPage - 1);
              if (tableContainerRef.current) {
                tableContainerRef.current.scrollTop = 0;
              }
              if (fetchedPages[newPage - 1]) {
                setSearchResultData(allPagesData[newPage - 1] || []);
              } else if (searchParams) {
                const newOffset = (newPage - 1) * rowsPerPage;
                const params = { ...searchParams };
                params.offset = newOffset;
                dispatch(searchMaxCapAction(params));
              }
            }}
            variant="outlined"
            shape="rounded"
            color="primary"
            size="small"
            sx={{ mt: 1, mb: 1, display: "flex", justifyContent: "flex-end" }}
          />
          <Grid container spacing={1}>
            <Grid item xs={12} container justifyContent="flex-end" spacing={2}>
              <Grid item>
                <div className="parentPoButtons">
                  <Button
                    className="moqButtons"
                    variant="contained"
                    color="primary"
                    disabled={selectedRows.length === 0 || hasEmptyQty}
                    onClick={handleUpdate}
                  >
                    Update Selected
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}

      {/* ── ADD NEW ITEM MODAL ── */}
      <Dialog
        open={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setAddItemId("");
          setAddPackId("");
          setAddMaxQty("");
          setAddPackIdError("");
          setAddMaxQtyError("");
          setAddItemIdError("");
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            fontSize: "16px",
            fontWeight: "bold",
            padding: "6px 16px",
          }}
        >
          {MAX_CAP_ADD_NEW_PIN_TITLE}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }} className="labelControl">
          <p className="addItemInfoText">
            Max Cap Configuration for a item will be added across <strong>ALL STORES</strong>.
          </p>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <LabelWithControl label="PIN" labelId="addPackId" required>
                <TextField
                  id="addPackId"
                  variant="outlined"
                  size="small"
                  placeholder="Single Pin"
                  value={addPackId}
                  onChange={(e) => { setAddPackId(e.target.value.replace(/[^0-9]/g, "").slice(0, 9)); setAddPackIdError(""); }}
                  error={!!addPackIdError}
                  helperText={addPackIdError}
                  FormHelperTextProps={{ style: { fontSize: "9px", margin: "3px 0" } }}
                  inputProps={{ style: { padding: "4px 10px", fontSize: 12 } }}
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={6}>
              <LabelWithControl label="MIN" labelId="addItemId">
                <TextField
                  id="addItemId"
                  variant="outlined"
                  size="small"
                  placeholder="Single Min"
                  value={addItemId}
                  onChange={(e) => { setAddItemId(e.target.value.replace(/[^0-9]/g, "").slice(0, 9)); setAddItemIdError(""); }}
                  error={!!addItemIdError}
                  helperText={addItemIdError}
                  FormHelperTextProps={{ style: { fontSize: "9px", margin: "3px 0" } }}
                  inputProps={{ style: { padding: "4px 10px", fontSize: 12 } }}
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={6}>
              <LabelWithControl
                label="Max Quantity"
                labelId="addMaxQty"
                required
              >
                <TextField
                  id="addMaxQty"
                  variant="outlined"
                  size="small"
                  placeholder="Max Quantity (1-99)"
                  value={addMaxQty}
                  error={!!addMaxQtyError}
                  helperText={addMaxQtyError}
                  FormHelperTextProps={{ style: { fontSize: "9px", margin: "3px 0" } }}
                  inputProps={{ maxLength: 2, style: { padding: "4px 10px", fontSize: 12 } }}
                  onChange={(e) => {
                    const maxQtyValue = e.target.value.replace(/[^0-9]/g, "");
                    if (maxQtyValue === "" || (parseInt(maxQtyValue) >= 1 && parseInt(maxQtyValue) <= 99)) { setAddMaxQty(maxQtyValue); setAddMaxQtyError(""); }
                  }}
                />
              </LabelWithControl>
            </Grid>
          </Grid>

        </DialogContent>
        <DialogActions>
          <Button
            className="moqButtons"
            onClick={() => {
              setShowAddModal(false);
              setAddItemId("");
              setAddPackId("");
              setAddMaxQty("");
              setAddPackIdError("");
              setAddMaxQtyError("");
              setAddItemIdError("");
            }}
            sx={{ "&:hover": { backgroundColor: "#D3D3D3" }, color: "black", backgroundColor: "#D3D3D3", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button className="moqButtons" variant="contained" color="primary" onClick={handleAddSubmit}>
            Add to All Stores
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── UPDATE ALL ITEMS MODAL ── */}
      <Dialog open={showUpdateAllModal} onClose={() => setShowUpdateAllModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: "primary.main", color: "white", fontSize: "16px", fontWeight: "bold", padding: "6px 16px" }}>
          {MAX_CAP_BULK_UPDATE_TITLE}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }} className="labelControl">
          <p className="addItemInfoText">
            Max Cap Configuration for a item will be updated across <strong>ALL STORES</strong>.
          </p>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <LabelWithControl label="PIN" labelId="globalPin" required>
                <TextField
                  id="globalPin"
                  variant="outlined"
                  size="small"
                  placeholder="Single Pin"
                  value={globalPin}
                  onChange={(e) => { setGlobalPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 9)); setGlobalPinError(""); }}
                  error={!!globalPinError}
                  helperText={globalPinError}
                  FormHelperTextProps={{ style: { fontSize: "9px", margin: "3px 0" } }}
                  inputProps={{ style: { padding: "4px 10px", fontSize: 12 } }}
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={6}>
              <LabelWithControl label="Max Quantity" labelId="globalMaxQty" required>
                <TextField
                  id="globalMaxQty"
                  variant="outlined"
                  size="small"
                  placeholder="Max Quantity (1-99)"
                  value={globalMaxQty}
                  error={!!globalQtyError}
                  helperText={globalQtyError}
                  FormHelperTextProps={{ style: { fontSize: "9px", margin: "3px 0" } }}
                  inputProps={{ maxLength: 2, style: { padding: "4px 10px", fontSize: 12 } }}
                  onChange={(e) => {
                    const globalMaxQty = e.target.value.replace(/[^0-9]/g, "");
                    if (globalMaxQty === "" || (parseInt(globalMaxQty) >= 1 && parseInt(globalMaxQty) <= 99)) { setGlobalMaxQty(globalMaxQty); setGlobalQtyError(""); }
                  }}
                />
              </LabelWithControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            className="moqButtons"
            onClick={() => { setShowUpdateAllModal(false); setGlobalPin(""); setGlobalMaxQty(""); setGlobalPinError(""); setGlobalQtyError(""); }}
            sx={{ "&:hover": { backgroundColor: "#D3D3D3" }, color: "black", backgroundColor: "#D3D3D3", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button className="moqButtons" variant="contained" color="primary" onClick={handleGlobalUpdate}>
            Update All Stores
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DELETE MODAL ── */}
      <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: "primary.main", color: "white", fontSize: "16px", fontWeight: "bold", padding: "6px 16px" }}>
          {MAX_CAP_DELETE_PIN_TITLE}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }} className="labelControl">
          <p className="addItemInfoText">
            Max Cap Configuration for this PIN will be <strong>deleted across ALL STORES</strong>.
          </p>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <LabelWithControl label="PIN" labelId="deletePin" required>
                <TextField
                  id="deletePin"
                  variant="outlined"
                  size="small"
                  placeholder="Single Pin"
                  value={deletePin}
                  onChange={(e) => { setDeletePin(e.target.value.replace(/[^0-9]/g, "").slice(0, 9)); setDeletePinError(""); }}
                  error={!!deletePinError}
                  helperText={deletePinError}
                  FormHelperTextProps={{ style: { fontSize: "9px", margin: "3px 0" } }}
                  inputProps={{ style: { padding: "4px 10px", fontSize: 12 } }}
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={6}>
              <LabelWithControl label="MIN" labelId="deleteMin">
                <TextField
                  id="deleteMin"
                  variant="outlined"
                  size="small"
                  placeholder="Single Min"
                  value={deleteMin}
                  onChange={(e) => { setDeleteMin(e.target.value.replace(/[^0-9]/g, "").slice(0, 9)); setDeleteMinError(""); }}
                  error={!!deleteMinError}
                  helperText={deleteMinError}
                  FormHelperTextProps={{ style: { fontSize: "9px", margin: "3px 0" } }}
                  inputProps={{ style: { padding: "4px 10px", fontSize: 12 } }}
                />
              </LabelWithControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            className="moqButtons"
            onClick={() => { setShowDeleteModal(false); setDeletePin(""); setDeleteMin(""); setDeletePinError(""); setDeleteMinError(""); }}
            sx={{ "&:hover": { backgroundColor: "#D3D3D3" }, color: "black", backgroundColor: "#D3D3D3", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button className="moqButtons" variant="contained" color="error" onClick={handleDeleteSubmit}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};
